import { ErrorCode, type ErrorCodeValue, type ValidationDetails } from '@/types/api';

// -----------------------------------------------------------------------------
// The one error type the UI ever sees.
//
// Every failure - an HTTP error body, a timeout, a dropped connection, a
// response that is not the envelope - arrives at a component as an `ApiError`
// with a stable `code`. That is what lets a screen write
// `if (error.code === ErrorCode.FORBIDDEN)` instead of sniffing at shapes.
// -----------------------------------------------------------------------------

export class ApiError extends Error {
  readonly code: ErrorCodeValue;
  readonly status: number;
  readonly details: unknown;
  /**
   * The server's correlation id. Shown to staff in a copyable "Details"
   * affordance so a support escalation carries something findable in the logs -
   * never as the primary error text, which needs to be a sentence a human can
   * act on.
   */
  readonly requestId: string | null;

  constructor(params: {
    code: ErrorCodeValue;
    message: string;
    status?: number;
    details?: unknown;
    requestId?: string | null;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status ?? 0;
    this.details = params.details ?? null;
    this.requestId = params.requestId ?? null;
  }

  /** `VALIDATION_FAILED` details, when they are the field-map shape. */
  get fieldErrors(): ValidationDetails | null {
    if (this.code !== ErrorCode.VALIDATION_FAILED) return null;
    if (!this.details || typeof this.details !== 'object' || Array.isArray(this.details)) {
      return null;
    }

    const entries = Object.entries(this.details as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    );

    return entries.length > 0 ? Object.fromEntries(entries) : null;
  }

  /** True when trying the same request again could plausibly succeed. */
  get isRetryable(): boolean {
    return (
      this.code === ErrorCode.NETWORK_ERROR ||
      this.code === ErrorCode.TIMEOUT ||
      this.code === ErrorCode.SERVICE_UNAVAILABLE ||
      this.code === ErrorCode.EXTERNAL_SERVICE_ERROR ||
      this.code === ErrorCode.INTERNAL_ERROR
    );
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Staff-facing copy for a failure.
 *
 * The server's own message is used for the cases where it is genuinely the most
 * useful thing to say (a validation failure names the field; a conflict names
 * what conflicted). Everything else gets copy written for an operator: an
 * INTERNAL_ERROR should read as "this is our fault, here is the id to quote",
 * not as whatever string happened to escape a catch block.
 */
export function describeError(error: unknown): { title: string; description: string } {
  if (!isApiError(error)) {
    return {
      title: 'Something went wrong',
      description:
        error instanceof Error && error.message
          ? error.message
          : 'An unexpected error occurred. Try again.',
    };
  }

  switch (error.code) {
    case ErrorCode.FORBIDDEN:
    case ErrorCode.ROLE_MISMATCH:
      return {
        title: 'You do not have permission',
        description: 'This action needs a higher access level. Ask a super admin.',
      };
    case ErrorCode.AUTH_REQUIRED:
    case ErrorCode.AUTH_INVALID:
    case ErrorCode.AUTH_EXPIRED:
      return {
        title: 'Your session ended',
        description: 'Sign in again to continue.',
      };
    case ErrorCode.ACCOUNT_SUSPENDED:
      return {
        title: 'Account not active',
        description: 'This admin account is suspended. Contact a super admin.',
      };
    case ErrorCode.RATE_LIMITED:
      return {
        title: 'Too many requests',
        description: 'Slow down for a moment, then try again.',
      };
    case ErrorCode.RESOURCE_NOT_FOUND:
      return {
        title: 'Not found',
        description: 'This record no longer exists. It may have been removed.',
      };
    case ErrorCode.RESOURCE_CONFLICT:
      return {
        title: 'Already changed',
        description: error.message || 'Someone else changed this first. Reload and try again.',
      };
    case ErrorCode.VALIDATION_FAILED:
      return {
        title: 'Check the form',
        description: error.message || 'Some fields need attention.',
      };
    case ErrorCode.TIMEOUT:
      return {
        title: 'Request timed out',
        description: 'The server took too long to respond. Try again.',
      };
    case ErrorCode.NETWORK_ERROR:
      return {
        title: 'Cannot reach the server',
        description: 'Check your connection, then try again.',
      };
    case ErrorCode.SERVICE_UNAVAILABLE:
    case ErrorCode.EXTERNAL_SERVICE_ERROR:
      return {
        title: 'Service unavailable',
        description: 'A service we depend on is down. Try again shortly.',
      };
    case ErrorCode.APP_CHECK_FAILED:
      return {
        title: 'Request blocked',
        description: 'This browser failed app attestation. Contact engineering.',
      };
    default:
      return {
        title: 'Something went wrong',
        description: 'This is a problem on our end. Quote the request id if you report it.',
      };
  }
}
